
import { randomPlayingIndex } from './fns'
import { useState, useRef, useCallback, useMemo } from 'react'
import { useGain, useAudioContext } from 'web-audio-hooks/react'

export { AudioProvider } from 'web-audio-hooks/react'

/**
 * Represents the playback mode for audio tracks
 * @typedef {('Shuffle' | 'SingleOnce' | 'SingleLoop' | 'SequentialOnce' | 'SequentialLoop')} PlayMode
 * - 'Shuffle' - Shuffle playback mode
 * - 'SingleLoop' - Repeat One playback mode
 * - 'SingleOnce' - Play current track once then stop
 * - 'SequentialOnce' - Play all tracks once then stop
 * - 'SequentialLoop' - Repeat All playback mode
 */

export const MODE: Record<PlayMode, PlayMode> = {
  Shuffle: `Shuffle`,
  SingleOnce: `SingleOnce`,
  SingleLoop: `SingleLoop`,
  SequentialOnce: `SequentialOnce`,
  SequentialLoop: `SequentialLoop`,
}
const MODES = [`Shuffle`, `SingleOnce`, `SingleLoop`, `SequentialOnce`, `SequentialLoop`] as const

type PlayMode = (typeof MODES)[number]

/**
 * AudioControls interface for audio playback functionality
 */
export interface AudioControls {
  /** Start playing the current audio track */
  play: () => void
  /** Pause the current audio track */
  pause: () => void
  /** Toggle play between play and pause */
  togglePlay: () => void
  /** Skip to next track based on current play mode */
  next: () => void
  /** Go to previous track based on current play mode */
  prev: () => void
  /**
   * Seek to specific time in current track
   * @param time - Target time in seconds
   */
  seek: (time: number) => void
  /**
   * Fast forward by specified milliseconds
   * @param ms - Number of milliseconds to fast forward (default: 5000)
   */
  fastForward: (ms?: number) => void
  /**
   * Rewind by specified milliseconds
   * @param ms - Number of milliseconds to rewind (default: 5000)
   */
  rewind: (ms?: number) => void
  /**
   * Set audio volume
   * @param volume - Volume level between 0 and 1
   */
  setVolume: (volume: number) => void
  /**
   * Change playback mode
   * @param mode - Optional specific play mode to set, cycles through modes if not provided
   */
  nextPlayMode: (mode?: PlayMode) => void
  /**
   * Play specific track by index
   * @param index - Index of track in playlist
   */
  playTrack: (index: number) => void
  /**
   * Set playback speed
   * @param rate - Playback rate between 0.5 and 3.0
   */
  setPlaybackRate: (rate: number) => void
  /**
   * Set the list of audio URLs
   * @param urls - Array of URLs for audio tracks
   */
  setAudioList: (urls: string[]) => void
  /**
   * Switch to a specific track in the playlist without playing it
   * @param index - Index of track to switch to
   */
  switchAudio: (index: number) => void
}
/**
 * State interface for audio playback functionality
 */
export interface AudioState {
  audios: string[]
  /** Whether audio is currently playing */
  playing: boolean
  /** Index of currently playing track in playlist */
  playingIndex: number
  /** Current playback position in seconds */
  currentTime: number
  /** Total duration of current track in seconds */
  duration: number
  /** Current volume level between 0 and 1 */
  volume: number
  /** Current playback speed between 0.5 and 3.0 */
  playbackRate: number
  /** Current playback mode (sequential/shuffle/loop) */
  playMode: PlayMode
}

interface AudioEvents {
  onEnded?: () => void
}

export type AudioOptions = AudioEvents

/**
 * Return type for useAudioList hook
 * @property state - Current state of audio playback including playing status, time, volume etc
 * @property controls - Methods to control audio playback like play, pause, seek etc
 * @property setAudioList - Function to update the list of audio URLs
 */
export type UseAudioListReturn = {
  state: AudioState
  controls: AudioControls
}

/**
 * React hook for managing a list of audio tracks with playback controls
 * @param urls - Array of URLs for audio tracks to be played
 * @returns Object containing playback state, controls, and URL setter
 * @example
 * ```tsx
 * const { state, controls, setAudioList } = useAudioList([
 *   'audio1.mp3',
 *   'audio2.mp3'
 * ])
 * ```
 */
export const useAudioList = (urls: string[], {
  onEnded,
}: AudioOptions = {}): UseAudioListReturn  => {
  const context = useAudioContext()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioPoolRef = useRef<HTMLAudioElement[]>([])
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const gainNode = useGain(0.5)
  const [playMode, setPlayMode] = useState<AudioState['playMode']>(MODE.SequentialOnce)
  const [playingIndex, setPlayingIndex] = useState<AudioState['playingIndex']>(-1)

  const [audioUrls, setAudioList] = useState(urls)
  const [playing, setPlaying] = useState<AudioState['playing']>(false)
  const [duration, setDuration] = useState<AudioState['duration']>(0)
  const [volume, setVolume] = useState<AudioState['volume']>(0.5)
  const [currentTime, setCurrentTime] = useState<AudioState['currentTime']>(0)
  const [playbackRate, setPlaybackRate] = useState<AudioState['playbackRate']>(1)

  const state: AudioState = useMemo(() => ({
    audios: audioUrls,
    volume,
    playing,
    duration,
    currentTime,
    playbackRate,
    playMode,
    playingIndex,
  }), [
    audioUrls,
    volume,
    playing,
    duration,
    currentTime,
    playbackRate,
    playMode,
    playingIndex,
  ])

  const getAudio = (url?: string) => {
    const available = audioPoolRef.current.find((a) => a.ended)
    if (available && url) {
      available.src = url
      return available
    }
    return new Audio(url)
  }

  const initAudio = useCallback(
    (url: string) => {
      if (!context || !url || !gainNode) return
      if (sourceRef.current) {
        audioRef.current?.pause()
        sourceRef.current.disconnect()
      }

      audioRef.current = getAudio(url)
      const audio = audioRef.current
      audio.playbackRate = playbackRate
      audio.crossOrigin = `anonymous`

      sourceRef.current = context.createMediaElementSource(audio)
      sourceRef.current.connect(gainNode)
      gainNode.connect(context.destination)

      const updateTime = () => {
        setCurrentTime(audio.currentTime)
      }

      const updateMetadata = () => {
        setDuration(audio.duration)
      }

      const onAudioEnded = () => {
        onEnded?.()
        if (playMode === `SingleLoop`) {
          audio.currentTime = 0
          audio.play()
        } else if (playMode === `SingleOnce`) {
          setPlaying(false)
          audioRef.current?.pause()
        } else { // SequentialLoop, Shuffle, and SequentialOnce
          const nextIndex = getNextIndex(true) // Pass true to indicate 'Once' modes
          if (nextIndex !== -1) {
            playTrack(nextIndex)
          } else if (playMode === `SequentialOnce`) { // If no next track in SequentialOnce, stop
            setPlaying(false)
            audioRef.current?.pause()
          }
        }
        audioPoolRef.current.push(audio)
      }

      const onRateChange = () => {
        controls.setPlaybackRate(audio.playbackRate)
      }

      audio.addEventListener(`ratechange`, onRateChange)
      audio.addEventListener(`timeupdate`, updateTime)
      audio.addEventListener(`loadedmetadata`, updateMetadata)
      audio.addEventListener(`ended`, onAudioEnded)

      return () => {
        audio.pause()
        audioPoolRef.current.forEach(a => a.pause())
        audioPoolRef.current = []
        sourceRef.current?.disconnect()
        audio.removeEventListener(`ratechange`, onRateChange)
        audio.removeEventListener(`timeupdate`, updateTime)
        audio.removeEventListener(`loadedmetadata`, updateMetadata)
        audio.removeEventListener(`ended`, onAudioEnded)
      }
    },
    [context, gainNode, playbackRate, playMode]
  )

  const playTrack = useCallback(async (index: number) => {
    if (index < 0 || index >= audioUrls.length) {
      console.warn(`Invalid index: ${index}`)
      // If the index is invalid (e.g., end of SequentialOnce playlist), stop playing
      if (playMode === `SequentialOnce`) {
        setPlaying(false)
        audioRef.current?.pause()
      }
      return
    }

    setPlayingIndex(index)

    setPlaying(false)
    initAudio(audioUrls[index])

    try {
      if (context?.state === `suspended`) {
        await context.resume()
      }
      await audioRef.current?.play()
      setPlaying(true)
    } catch (err) {
      console.error(`播放失败:`, err)
    }

  }, [context, audioUrls, initAudio, playMode])

  const getPrevIndex = () => {
    if (audioUrls.length === 0) return -1
    if (playMode === `Shuffle`) {
      return randomPlayingIndex(playingIndex, audioUrls.length)
    }
    if (playingIndex <= 0) {
      return audioUrls.length - 1
    }
    return playingIndex - 1
  }

  // Add `isFromEndedEvent` to handle 'Once' modes
  const getNextIndex = (isFromEndedEvent = false) => {
    if (audioUrls.length === 0) return -1
    if (audioUrls.length > 0 && playingIndex === -1) {
      return 0
    }
    if (playMode === `Shuffle`) {
      return randomPlayingIndex(playingIndex, audioUrls.length)
    }

    if (playMode === `SingleOnce`) {
      return -1 // No next track for `SingleOnce`
    }

    // For `SequentialOnce` and `SequentialLoop`
    if (playingIndex >= (audioUrls.length - 1)) {
      if (playMode === `SequentialLoop`) {
        return 0 // Loop back to the beginning
      } else if (playMode === `SequentialOnce` && isFromEndedEvent) {
        return -1 // Stop if at the end of SequentialOnce and triggered by onEnded
      }
    }

    return playingIndex + 1
  }

  const playPrevTrack = () => {
    playTrack(getPrevIndex())
  }

  const playNextTrack = () => {
    playTrack(getNextIndex())
  }

  const controls: AudioControls = {
    play: async () => {
      if (audioRef.current) {
        await audioRef.current.play()
        setPlaying(true)
      } else {
        if (audioUrls.length > 0) {
          playTrack(0)
        }
      }
    },
    pause: () => {
      audioRef.current?.pause()
      setPlaying(false)
    },
    togglePlay: () => {
      playing ? controls.pause() : controls.play()
    },
    playTrack,
    setAudioList,
    prev: playPrevTrack,
    next: playNextTrack,
    seek: (time: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time
      }
    },
    fastForward: (ms: number = 5000) => {
      if (audioRef.current) {
        const newTime = Math.min(audioRef.current.currentTime + ms / 1000, audioRef.current.duration || 0)
        audioRef.current.currentTime = newTime
      }
    },
    rewind: (ms: number = 5000) => {
      if (audioRef.current) {
        const newTime = Math.max(audioRef.current.currentTime - ms / 1000, 0)
        audioRef.current.currentTime = newTime
      }
    },
    setVolume: (volume: number) => {
      setVolume(volume)
      if (gainNode) {
        gainNode.gain.setValueAtTime(volume, context?.currentTime || 0)
      }
    },
    nextPlayMode: (_mode?: PlayMode) => {
      const currentIndex = MODES.indexOf(playMode)
      const nextIndex = (currentIndex + 1) % MODES.length
      const mode = _mode || MODES[nextIndex]
      setPlayMode(mode)
    },
    setPlaybackRate: (rate: number) => {
      rate = Math.min(Math.max(rate, 0.5), 3.0)
      setPlaybackRate(rate)
      if (audioRef.current) {
        audioRef.current.playbackRate = rate
      }
    },
    switchAudio: setPlayingIndex
  }

  return {
    state,
    controls,
  }
}
